import React, { useState, useEffect, useContext, useCallback } from "react";
import classNames from "classnames/bind";
import styles from "./UserManagement.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faKey } from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_API_URL, BASE_USERS_URL } from "~/helper/apiService";
import Skeleton from "~/components/Skeleton/Skeleton";

const cx = classNames.bind(styles);

// Helper function để chuẩn hóa role
const normalizeRole = (role) => {
  if (!role) return role;
  const roleLower = role.toLowerCase();
  if (roleLower === "dispatchers") return "Dispatchers";
  if (roleLower === "accounting") return "accounting";
  return role; // Giữ nguyên cho admin, crm, driver
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "crm",
    drive_number: "",
  });
  const [vehicleNumbers, setVehicleNumbers] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal: showConfirmModal } = useContext(ModalContext);

  const fetchVehicleNumbers = useCallback(async () => {
    try {
      setLoadingVehicles(true);
      const response = await fetch(`${BASE_API_URL}api/revenue/vehicle-numbers`, {
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setVehicleNumbers(data.data || []);
      } else {
        console.error("Lỗi khi lấy danh sách số xe:", data.message);
      }
    } catch (error) {
      console.error("Lỗi kết nối khi lấy danh sách số xe:", error);
    } finally {
      setLoadingVehicles(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_USERS_URL}`, {
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        // Chuẩn hóa role cho mỗi user
        const normalizedUsers = (data.users || []).map((user) => ({
          ...user,
          role: normalizeRole(user.role),
        }));
        setUsers(normalizedUsers);
      } else {
        showToast(data.error || "Lỗi khi lấy danh sách users", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchUsers();
    fetchVehicleNumbers();
  }, [fetchUsers, fetchVehicleNumbers]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "crm",
      drive_number: "",
    });
    setShowModal(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "crm",
      drive_number: user.drive_number || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          name: formData.name,
          email: formData.email,
          role: formData.role,
        };
        
        // Chỉ thêm drive_number nếu role là driver
        if (formData.role === "driver") {
          updateData.drive_number = formData.drive_number || null;
        } else {
          updateData.drive_number = null;
        }

        const response = await fetch(`${BASE_USERS_URL}/${editingUser.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updateData),
        });

        const data = await response.json();
        if (response.ok) {
          showToast("Cập nhật user thành công", "success");
          setShowModal(false);
          fetchUsers();
        } else {
          showToast(data.error || "Lỗi khi cập nhật user", "error");
        }
      } else {
        // Create user
        if (!formData.password) {
          showToast("Vui lòng nhập mật khẩu", "error");
          return;
        }

        const createData = {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        };
        
        // Chỉ thêm drive_number nếu role là driver
        if (formData.role === "driver") {
          createData.drive_number = formData.drive_number || null;
        }

        const response = await fetch(`${BASE_USERS_URL}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(createData),
        });

        const data = await response.json();
        if (response.ok) {
          showToast("Tạo user thành công", "success");
          setShowModal(false);
          fetchUsers();
        } else {
          showToast(data.error || "Lỗi khi tạo user", "error");
        }
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const handleDelete = async (userId, userName) => {
    try {
      const response = await fetch(`${BASE_USERS_URL}/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      const data = await response.json();
      if (response.ok) {
        showToast("Xóa user thành công", "success");
        fetchUsers();
      } else {
        showToast(data.error || "Lỗi khi xóa user", "error");
        return false;
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
      return false;
    }
  };

  const confirmDelete = (user) => {
    showConfirmModal({
      title: "Xác Nhận Xóa",
      message: `Bạn có chắc chắn muốn xóa user "${user.name}" (${user.email})?`,
      onConfirm: () => handleDelete(user.id, user.name),
      onCancel: null,
    });
  };

  const handleChangePassword = async (userId) => {
    const newPassword = prompt("Nhập mật khẩu mới:");
    if (!newPassword) {
      return;
    }

    try {
      const response = await fetch(`${BASE_USERS_URL}/${userId}/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ newPassword }),
      });

      const data = await response.json();
      if (response.ok) {
        showToast("Đổi mật khẩu thành công", "success");
      } else {
        showToast(data.error || "Lỗi khi đổi mật khẩu", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    // Chuẩn hóa role mới
    const normalizedNewRole = normalizeRole(newRole);
    
    // Optimistic update - cập nhật UI ngay lập tức
    const oldUsers = [...users];
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, role: normalizedNewRole } : user
      )
    );

    try {
      const response = await fetch(`${BASE_USERS_URL}/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ role: newRole }),
      });

      const data = await response.json();
      if (response.ok) {
        showToast(`Đổi role thành công: ${newRole}`, "success");
        // Fetch lại để đảm bảo đồng bộ với server
        fetchUsers();
      } else {
        // Revert lại nếu có lỗi
        setUsers(oldUsers);
        showToast(data.error || "Lỗi khi đổi role", "error");
        fetchUsers(); // Refresh to revert change
      }
    } catch (error) {
      // Revert lại nếu có lỗi
      setUsers(oldUsers);
      showToast("Lỗi kết nối: " + error.message, "error");
      fetchUsers(); // Refresh to revert change
    }
  };


  return (
    <div className={cx("user-management")}>
      <div className={cx("header")}>
        <h2 className={cx("subtitle")}>Quản Lý User</h2>
        <button className={cx("add-button")} onClick={handleAdd}>
          <FontAwesomeIcon icon={faPlus} /> Thêm User
        </button>
      </div>

      {loading ? (
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th")}>Tên</th>
              <th className={cx("th")}>Email</th>
              <th className={cx("th")}>Role</th>
              <th className={cx("th")}>Số xe</th>
              <th className={cx("th")}>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 7 }).map((_, idx) => (
              <tr key={idx} className={cx("tr")}>
                <td className={cx("td")}>
                  <Skeleton height={14} width="70%" />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={14} width="85%" />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={28} width={130} radius={8} />
                </td>
                <td className={cx("td")}>
                  <Skeleton height={14} width="45%" />
                </td>
                <td className={cx("td")}>
                  <div className={cx("action-buttons")}>
                    <Skeleton height={30} width={30} radius={8} />
                    <Skeleton height={30} width={30} radius={8} />
                    <Skeleton height={30} width={30} radius={8} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : users.length === 0 ? (
        <p className={cx("no-data")}>Không có user nào</p>
      ) : (
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th")}>Tên</th>
              <th className={cx("th")}>Email</th>
              <th className={cx("th")}>Role</th>
              <th className={cx("th")}>Số xe</th>
              <th className={cx("th")}>Hành Động</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className={cx("tr")}>
                <td className={cx("td")}>{user.name}</td>
                <td className={cx("td")}>{user.email}</td>
                <td className={cx("td")}>
                  <select
                    value={user.role}
                    onChange={(e) => handleChangeRole(user.id, e.target.value)}
                    className={cx("select-input", `role-${user.role}`)}
                  >
                    <option value="admin">Admin</option>
                    <option value="crm">CRM</option>
                    <option value="driver">Driver</option>
                    <option value="Dispatchers">Điều phối</option>
                    <option value="accounting">Kế toán</option>
                  </select>
                </td>
                <td className={cx("td")}>
                  {user.role === "driver" ? (user.drive_number || "—") : "—"}
                </td>
                <td className={cx("td")}>
                  <div className={cx("action-buttons")}>
                    <button
                      className={cx("action-button", "edit-button")}
                      onClick={() => handleEdit(user)}
                      title="Sửa"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className={cx("action-button", "password-button")}
                      onClick={() => handleChangePassword(user.id)}
                      title="Đổi mật khẩu"
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </button>
                    <button
                      className={cx("action-button", "delete-button")}
                      onClick={() => confirmDelete(user)}
                      title="Xóa"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className={cx("modal-overlay")} onClick={() => setShowModal(false)}>
          <div className={cx("modal-content")} onClick={(e) => e.stopPropagation()}>
            <h3 className={cx("modal-title")}>
              {editingUser ? "Sửa User" : "Thêm User Mới"}
            </h3>
            <form 
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                // Ngăn form submit khi nhấn Enter trong input password
                if (e.key === "Enter" && e.target.type === "password") {
                  e.preventDefault();
                }
              }}
            >
              <div className={cx("form-group")}>
                <label>Tên *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className={cx("input")}
                />
              </div>

              <div className={cx("form-group")}>
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className={cx("input")}
                />
              </div>

              {!editingUser && (
                <div className={cx("form-group")}>
                  <label>Mật khẩu *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      // Ngăn form submit khi nhấn Enter trong input password
                      if (e.key === "Enter") {
                        e.preventDefault();
                      }
                    }}
                    required
                    className={cx("input")}
                  />
                </div>
              )}

              <div className={cx("form-group")}>
                <label>Role *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  required
                  className={cx("input")}
                >
                  <option value="admin">Admin</option>
                  <option value="crm">CRM</option>
                  <option value="driver">Driver</option>
                  <option value="Dispatchers">Điều phối</option>
                  <option value="accounting">Kế toán</option>
                </select>
              </div>

              {formData.role === "driver" && (
                <div className={cx("form-group")}>
                  <label>Số xe</label>
                  <select
                    name="drive_number"
                    value={formData.drive_number}
                    onChange={handleInputChange}
                    className={cx("input")}
                  >
                    <option value="">— Chọn số xe —</option>
                    {loadingVehicles ? (
                      <option disabled>Đang tải...</option>
                    ) : (
                      vehicleNumbers.map((vehicle) => (
                        <option key={vehicle} value={vehicle}>
                          {vehicle}
                        </option>
                      ))
                    )}
                  </select>
                  <small className={cx("form-hint")}>
                    Chọn số xe để driver có thể xem doanh thu và tổng nợ của xe đó
                  </small>
                </div>
              )}

              <div className={cx("modal-actions")}>
                <button type="submit" className={cx("submit-button")}>
                  {editingUser ? "Cập nhật" : "Tạo"}
                </button>
                <button
                  type="button"
                  className={cx("cancel-button")}
                  onClick={() => setShowModal(false)}
                >
                  Hủy
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
